import { withLoading } from "@/api/api";
import { get, normalizarPagina } from "./http";
import { BENEFICIOS } from "./apuracao-projeto.service";

/**
 * Service da tela `apuracao-por-colaborador.vue` (API real).
 *
 * `GET .../apuracao/colaboradores` apura **uma competência por vez**: o custo do mês de cada
 * colaborador é rateado entre Pesquisa e Desenvolvimento pelas horas de timesheet
 * classificadas (o resto fica sem fase). É paginado (`data` + `meta`).
 * `GET .../apuracao/colaboradores/{funcionarioId}` traz a folha do mês por rubrica, aberta
 * por projeto × fase (linha expandida) — esse não é paginado.
 *
 * @typedef {object} ApuracaoColaboradorLinha
 * @property {number} id - `funcionario_id`
 * @property {string} colaborador
 * @property {string} matricula
 * @property {number} horas - horas trabalhadas no mês
 * @property {number} horasPesquisa
 * @property {number} horasDesenvolvimento
 * @property {number} horasSemFase
 * @property {number} custo - custo do mês (folha)
 * @property {number} custoPesquisa
 * @property {number} custoDesenvolvimento
 * @property {number} custoSemFase - derivado (`custo - pesquisa - desenvolvimento`)
 * @property {boolean} folhaCalculada - a folha da competência foi encontrada
 * @property {object} [detalhe] - folha por rubrica/projeto (carregada ao expandir a linha)
 */

/**
 * Coluna da tela → campo aceito na ordenação da API. O back-end ordena **só** por
 * `colaborador` e `matricula`; qualquer outro `sort[...]` é ignorado — por isso a tela
 * oferece ordenação apenas nessas colunas.
 */
export const CAMPOS_ORDENACAO = {
  colaborador: "colaborador",
  matricula: "matricula",
};

/** A coluna da tela é ordenável na API? (usado nos cabeçalhos) */
export const ehOrdenavel = (campo) => Object.prototype.hasOwnProperty.call(CAMPOS_ORDENACAO, campo);

const LOADING_KEY = "apuracaocapex-apuracao-colaborador";

/** Número seguro. */
const numero = (valor) => Number(valor) || 0;

/** Arredonda para 2 casas decimais. */
const decimal = (valor) => Math.round(numero(valor) * 100) / 100;

/** Texto de filtro, sem espaços nas pontas. */
const texto = (valor) => String(valor || "").trim();

/** `MM/AAAA` (formato da tela) → `AAAA-MM` (formato da API). */
export const competenciaParaApi = (competencia) => {
  const partes = String(competencia || "").match(/^(\d{2})\/(\d{4})$/);

  return partes ? `${partes[2]}-${partes[1]}` : "";
};

/** As 12 competências do ano-base, no formato `MM/AAAA` do filtro. */
export const listarCompetencias = (anoBase) =>
  Array.from({ length: 12 }, (_, indice) => `${String(indice + 1).padStart(2, "0")}/${anoBase}`);

/**
 * Desloca a competência em `passo` meses, **sem sair do ano-base** (ponta trava no mês 1/12).
 *
 * @param {string} competencia - `MM/AAAA`
 * @param {number} passo - `-1` mês anterior · `1` mês seguinte
 * @param {string} anoBase
 * @returns {string} competência no formato `MM/AAAA`
 */
export const deslocarCompetencia = (competencia, passo, anoBase) => {
  const partes = String(competencia || "").match(/^(\d{2})\/(\d{4})$/);
  const mes = numero(partes && partes[1]) + passo;

  if (mes < 1) return `01/${anoBase}`;
  if (mes > 12) return `12/${anoBase}`;

  return `${String(mes).padStart(2, "0")}/${anoBase}`;
};

/** Fase da API (`1` pesquisa · `2` desenvolvimento · `null` sem fase) → chave da tela. */
const faseParaTela = (fase) => {
  if (Number(fase) === 1) return "pesquisa";
  if (Number(fase) === 2) return "desenvolvimento";

  return "semFase";
};

/** Os filtros aceitos pela API (`filters[colaborador]`, `filters[matricula]`, `filters[projeto]`). */
const montarFiltros = ({ colaborador, matricula, projeto } = {}) => {
  const termos = (valor) => (texto(valor) ? [texto(valor)] : []);

  return {
    colaborador: termos(colaborador),
    matricula: termos(matricula),
    projeto: termos(projeto),
  };
};

/** Ordenação do `v-data-table` (`[{field, desc}]`) no formato da API (`{campo: "asc"}`). */
const montarSort = (sort) => (Array.isArray(sort)
  ? sort.reduce((acumulado, item) => {
    const campo = item && CAMPOS_ORDENACAO[item.field];

    return campo ? { ...acumulado, [campo]: item.desc ? "desc" : "asc" } : acumulado;
  }, {})
  : sort);

/** Uma linha da tela a partir de um colaborador da API. */
const montarLinha = (item) => {
  const custo = numero(item.custo);
  const pesquisa = numero(item.pesquisa);
  const desenvolvimento = numero(item.desenvolvimento);

  return {
    id: item.funcionario_id,
    colaborador: item.colaborador,
    matricula: item.matricula,
    horas: numero(item.horas_trabalhadas),
    horasPesquisa: numero(item.horas_pesquisa),
    horasDesenvolvimento: numero(item.horas_desenvolvimento),
    horasSemFase: numero(item.horas_sem_fase),
    custo,
    custoPesquisa: pesquisa,
    custoDesenvolvimento: desenvolvimento,
    custoSemFase: decimal(custo - pesquisa - desenvolvimento),
    folhaCalculada: !!item.folha_calculada,
  };
};

/** Uma rubrica da folha, com o valor repartido pelas colunas (projeto × fase). */
const montarRubrica = (rubrica, colunas) => {
  const valores = rubrica.valores || [];
  const porFase = { pesquisa: 0, desenvolvimento: 0, semFase: 0 };

  colunas.forEach((coluna, indice) => {
    porFase[faseParaTela(coluna.fase)] += numero(valores[indice]);
  });

  return {
    id: rubrica.rubrica_id,
    nome: rubrica.nome,
    valor: numero(rubrica.valor),
    pesquisa: decimal(porFase.pesquisa),
    desenvolvimento: decimal(porFase.desenvolvimento),
    semFase: decimal(porFase.semFase),
  };
};

/** Rótulo que fecha a folha: é o total das rubricas, então vai sempre por último. */
const RUBRICA_TOTAL = "total de vencimentos";

/** Nome comparável: sem acentos, minúsculo e sem espaços nas pontas. */
const chaveOrdenacao = (nome) => String(nome || "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLowerCase()
  .trim();

/** Rubricas em ordem **alfabética**, com `Total de Vencimentos` sempre no fim. */
const ordenarRubricas = (rubricas) => rubricas.slice().sort((a, b) => {
  const primeiro = chaveOrdenacao(a.nome);
  const segundo = chaveOrdenacao(b.nome);

  if (primeiro === RUBRICA_TOTAL) return 1;
  if (segundo === RUBRICA_TOTAL) return -1;

  return primeiro.localeCompare(segundo, "pt-BR");
});

/** Folha do colaborador (linha expandida): rubricas + alocação por projeto × fase. */
const montarFolha = (detalhe = {}) => {
  const colunas = detalhe.colunas || [];
  const custo = (detalhe.custo_total || {}).valores || [];
  const rubricas = (detalhe.rubricas || []).map((rubrica) => montarRubrica(rubrica, colunas));

  return {
    horasTrabalhadas: numero(detalhe.horas_trabalhadas),
    rubricas: ordenarRubricas(rubricas),
    alocacoes: colunas.map((coluna, indice) => ({
      id: `${coluna.projeto_id}-${faseParaTela(coluna.fase)}`,
      projeto: coluna.projeto,
      fase: faseParaTela(coluna.fase),
      horas: numero(coluna.horas),
      percentual: numero(coluna.percentual),
      valor: numero(custo[indice]),
    })),
  };
};

/**
 * Apuração por colaborador de **uma competência** (paginada, ordenada e filtrada na API).
 *
 * @param {object} params
 * @param {number|string} params.empresaId
 * @param {string|number} params.anoBase
 * @param {string} params.competencia - `MM/AAAA` (uma por vez)
 * @param {number} [params.beneficio] - `1` Lei do Bem · `2` Lei de Informática
 * @param {number} [params.page]
 * @param {number} [params.perPage]
 * @param {Array|object} [params.sort]
 * @param {{ colaborador?: string, matricula?: string, projeto?: string }} [params.filtros]
 * @returns {Promise<{ rows: ApuracaoColaboradorLinha[], total: number, page: number,
 *   perPage: number, totais: object }>}
 */
export const listarApuracaoColaborador = async ({
  empresaId,
  anoBase,
  competencia,
  beneficio = 1,
  page = 1,
  perPage = 15,
  sort = [],
  filtros = {},
} = {}) =>
  withLoading(LOADING_KEY, async () => {
    const corpo = await get(empresaId, "apuracao/colaboradores", {
      ano_base: Number(anoBase) || undefined,
      trabalho: beneficio,
      competencia: competenciaParaApi(competencia) || undefined,
      page,
      per_page: Number(perPage) > 0 ? Number(perPage) : undefined,
      filters: montarFiltros(filtros),
      sort: montarSort(sort),
    });

    const pagina = normalizarPagina(corpo, { perPage });

    return { ...pagina, rows: pagina.rows.map(montarLinha) };
  });

/**
 * Folha do colaborador por rubrica, aberta por projeto × fase (linha expandida).
 *
 * @param {object} params
 * @param {number|string} params.empresaId
 * @param {string|number} params.anoBase
 * @param {string} params.competencia - `MM/AAAA`
 * @param {number} [params.beneficio]
 * @param {number|string} params.funcionarioId
 * @param {{ projeto?: string }} [params.filtros] mesmos filtros da listagem (`projeto`)
 * @returns {Promise<{ horasTrabalhadas: number, rubricas: Array, alocacoes: Array }>}
 */
export const obterFolhaColaborador = async ({
  empresaId,
  anoBase,
  competencia,
  beneficio = 1,
  funcionarioId,
  filtros = {},
}) =>
  withLoading(LOADING_KEY, async () => {
    const corpo = await get(empresaId, `apuracao/colaboradores/${funcionarioId}`, {
      ano_base: Number(anoBase) || undefined,
      trabalho: beneficio,
      competencia: competenciaParaApi(competencia) || undefined,
      filters: montarFiltros(filtros),
    });

    return montarFolha((corpo && corpo.data) || {});
  });

export { BENEFICIOS };

export default {
  listarApuracaoColaborador,
  obterFolhaColaborador,
  listarCompetencias,
  deslocarCompetencia,
  competenciaParaApi,
  ehOrdenavel,
  BENEFICIOS,
};
